import React, { useState } from 'react';
import { CreditCard, Lock, Check, Loader2, AlertCircle, Shield, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { apiService } from '@/lib/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 29,
    description: 'For small projects',
    features: ['Up to 2 active projects', 'Bid management', 'Draw scheduling', 'Email support'],
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 69,
    description: 'For growing businesses',
    features: ['Up to 5 active projects', 'Everything in Basic', 'AI budget analysis', 'Priority support'],
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 199,
    description: 'For large operations',
    features: ['Unlimited projects', 'Everything in Pro', 'Cash flow forecasting', 'Dedicated support'],
    popular: false,
  },
];

const TierSelector = ({ onSelect }) => (
  <div className="space-y-4" data-testid="tier-selector">
    <div className="space-y-3">
      {TIERS.map((tier) => (
        <button
          key={tier.id}
          onClick={() => onSelect(tier)}
          className={`w-full text-left p-4 rounded-lg border-2 transition-all hover:shadow-md ${
            tier.popular
              ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
          data-testid={`tier-option-${tier.id}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">{tier.name}</span>
                {tier.popular && (
                  <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">Most Popular</span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{tier.description}</p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-slate-900">${tier.price}</span>
              <span className="text-sm text-slate-500">/mo</span>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {tier.features.map((f, i) => (
              <span key={i} className="text-xs text-slate-600 flex items-center gap-1">
                <Check className="w-3 h-3 text-emerald-500" /> {f}
              </span>
            ))}
          </div>
        </button>
      ))}
    </div>
  </div>
);

const CardForm = ({ user, selectedTier, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { data: intentData } = await apiService.createSetupIntent(user.id);

      if (!intentData?.client_secret) {
        throw new Error('No client secret received from server');
      }

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        intentData.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              email: user.email,
              name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            },
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setIsProcessing(false);
        return;
      }

      await apiService.updatePaymentMethod(user.id, setupIntent.payment_method, selectedTier.id);
      onSuccess();
    } catch (err) {
      console.error('Payment setup error:', err);
      setError(err.response?.data?.detail || 'Failed to set up payment method. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payment-card-form">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        data-testid="back-to-tiers-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Change plan
      </button>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
        <div>
          <span className="font-semibold text-slate-900">{selectedTier.name} Plan</span>
          <span className="text-sm text-slate-500 ml-2">${selectedTier.price}/mo after trial</span>
        </div>
        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
          30 days free
        </span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 border border-slate-200 rounded-lg bg-white">
          <CardElement
            options={{
              style: {
                base: { fontSize: '16px', color: '#1e293b', '::placeholder': { color: '#94a3b8' } },
              },
            }}
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2" data-testid="payment-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Lock className="w-4 h-4" />
          <span>Secured with 256-bit encryption. Cancel anytime.</span>
        </div>

        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full bg-blue-600 hover:bg-blue-700 h-11"
          data-testid="add-card-submit-btn"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Start Free Trial - {selectedTier.name} Plan
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

const PaymentRequiredModal = ({ isOpen, user, onSuccess }) => {
  const [selectedTier, setSelectedTier] = useState(null);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        style={{ zIndex: 200 }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="payment-required-modal"
      >
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <div className="p-3 bg-blue-100 rounded-full">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            {selectedTier ? 'Add Payment Method' : 'Choose Your Plan'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {selectedTier
              ? `You won't be charged for 30 days. Cancel anytime.`
              : 'Start with a 30-day free trial on any plan. No charge until the trial ends.'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          {!selectedTier ? (
            <TierSelector onSelect={setSelectedTier} />
          ) : (
            <Elements stripe={stripePromise}>
              <CardForm
                user={user}
                selectedTier={selectedTier}
                onSuccess={onSuccess}
                onBack={() => setSelectedTier(null)}
              />
            </Elements>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentRequiredModal;
